import React, { useState, useEffect, useLayoutEffect, useRef, Suspense } from 'react';
import { ArrowRight, Sun, Moon, Scan, Cpu, Box, Wand2, Sparkles, Server } from 'lucide-react';
import { X } from 'lucide-react';  // For close button
import ProjectPortfolio from './ProjectPortfolio';
import WorldHero from './WorldHero';
import useSmoothScroll from './useSmoothScroll';

const TetrisModal = React.lazy(() => import('./TetrisModal'));
const FlappyBirdModal = React.lazy(() => import('./FlappyBirdModal')); // Fix space after =

function getStored(key, fallback) {
    try {
      const v = localStorage.getItem(key); // Added 'const' for v
      return v ?? fallback;
    } catch {
      return fallback;
    }
  }

function applyThemeToDOM(theme) {
  document.documentElement.classList.toggle('dark', theme === 'dark');
}

function useTheme() {
  const initialMode = getStored('themeMode', 'system');
  const initialActual = initialMode === 'system'
    ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    : getStored('actualTheme', 'light');

  const [themeMode, setThemeMode] = useState(initialMode);
  const [actualTheme, setActualTheme] = useState(initialActual);
  const modeRef = useRef(initialMode);
  const mqRef = useRef(null);
  const handlerRef = useRef(null);

  useLayoutEffect(() => {
    applyThemeToDOM(actualTheme);
  }, [actualTheme]);

  useEffect(() => {
    try {
      window.localStorage.setItem('themeMode', themeMode);
      window.localStorage.setItem('actualTheme', actualTheme);
    } catch {}
  }, [themeMode, actualTheme]);

  useEffect(() => {
    modeRef.current = themeMode;

    if (mqRef.current && handlerRef.current) {
      mqRef.current.removeEventListener('change', handlerRef.current);
      handlerRef.current = null;
      mqRef.current = null;
    }

    if (themeMode === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e) => {
        if (modeRef.current !== 'system') return;
        setActualTheme(e.matches ? 'dark' : 'light');
      };
      mq.addEventListener('change', handleChange);
      mqRef.current = mq;
      handlerRef.current = handleChange;
      setActualTheme(mq.matches ? 'dark' : 'light');

      return () => {
        mq.removeEventListener('change', handleChange);
        handlerRef.current = null;
        mqRef.current = null;
      };
    }
  }, [themeMode]);

  const toggleTheme = () => {
    const next = actualTheme === 'dark' ? 'light' : 'dark';
    modeRef.current = 'manual';
    setThemeMode('manual');
    setActualTheme(next);
  };

  return { themeMode, actualTheme, toggleTheme };
}

export default function KhyaalTechLanding() {
  useSmoothScroll();
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [flappyOpen, setFlappyOpen] = useState(false); // Add this line
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [projectForm, setProjectForm] = useState({ name: '', email: '', details: '' });
  const { actualTheme, toggleTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [counters, setCounters] = useState({ clients: 0, satisfaction: 0, support: 0 });
  const [hasAnimated, setHasAnimated] = useState(false);
  const [tetrisOpen, setTetrisOpen] = useState(false);

  useEffect(() => {
    setMounted(true);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1 }
    );

    const id = setTimeout(() => {
      document.querySelectorAll('[data-animate]').forEach((el) => observer.observe(el));
    }, 100);

    return () => {
      clearTimeout(id);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!mounted || hasAnimated) return;

    const animateCounter = (key, target, duration = 2000) => {
      const start = 0;
      const increment = target / (duration / 16);
      let current = start;

      const timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          current = target;
          clearInterval(timer);
        }
        setCounters(prev => ({ ...prev, [key]: Math.floor(current) }));
      }, 16);

      return timer;
    };

    const timeout = setTimeout(() => {
      animateCounter('clients', 12, 1500);
      animateCounter('satisfaction', 98, 1800);
      animateCounter('support', 24, 1200);
      setHasAnimated(true);
    }, 1500);

    return () => clearTimeout(timeout);
  }, [mounted, hasAnimated]);

  if (!mounted) return null;

  const isVisible = (id) => visibleSections.has(id);

  const services = [
    {
      icon: Scan,
      title: "Computer Vision",
      description: "We engineer sophisticated visual perception systems that understand and interpret complex imagery with unprecedented accuracy. Our solutions span object detection, real-time tracking, scene understanding, and visual anomaly detection—deployed across manufacturing quality control, autonomous systems, and intelligent surveillance applications.",
      capabilities: ["Object Detection & Tracking", "Scene Understanding", "Visual Anomaly Detection", "3D Reconstruction"]
    },
    {
      icon: Cpu,
      title: "Agentic AI Systems",
      description: "Building autonomous intelligent agents that operate independently with minimal human oversight. These systems learn from experience, adapt to changing conditions, and execute sophisticated workflows—from automated customer service to complex decision-making in enterprise environments.",
      capabilities: ["Autonomous Decision Making", "Self-Learning Systems", "Workflow Automation", "Multi-Agent Coordination"]
    },
    {
      icon: Box,
      title: "3D Generation & Synthesis",
      description: "Creating photorealistic 3D environments and assets using advanced generative models. From architectural visualization to product design, we transform minimal input data into fully-realized three-dimensional spaces—enabling rapid prototyping and immersive experiences.",
      capabilities: ["Photorealistic Rendering", "Procedural Generation", "Asset Creation", "Environment Design"]
    },
    {
      icon: Wand2,
      title: "Image & Video Processing",
      description: "State-of-the-art manipulation and generation powered by custom-trained neural networks. Our research covers everything from image enhancement and style transfer to video synthesis and real-time processing—delivering production-ready solutions for creative and technical applications.",
      capabilities: ["Enhancement & Restoration", "Style Transfer", "Video Synthesis", "Real-time Processing"]
    },
    {
      icon: Sparkles,
      title: "Custom Model Training",
      description: "Specialized fine-tuning using Low-Rank Adaptation and advanced training techniques. We develop domain-specific models that deliver exceptional performance with minimal computational overhead—optimized for your unique use case and deployment constraints.",
      capabilities: ["LoRA Fine-tuning", "Domain Adaptation", "Model Optimization", "Transfer Learning"]
    },
    {
      icon: Server,
      title: "Enterprise SaaS Solutions",
      description: "Scalable AI infrastructure designed for mission-critical production deployments. We build robust, maintainable systems that integrate seamlessly with your existing technology stack—complete with monitoring, versioning, and continuous improvement pipelines.",
      capabilities: ["API Development", "Cloud Infrastructure", "System Integration", "Performance Monitoring"]
    }
  ];

  const technologies = [
    { category: "Core Frameworks", items: ["Python", "TensorFlow", "PyTorch", "JAX"] },
    { category: "Computer Vision", items: ["OpenCV", "YOLO", "Detectron2", "MediaPipe"] },
    { category: "NLP & LLMs", items: ["HuggingFace", "LangChain", "OpenAI", "Anthropic"] },
    { category: "Infrastructure", items: ["Docker", "Kubernetes", "AWS", "Azure"] },
    { category: "Development", items: ["FastAPI", "Flask", "Next.js", "React"] },
    { category: "Data & ML", items: ["Pandas", "NumPy", "Scikit-learn", "MLflow"] }
  ];

  const clients = [
    {
      index: "01",
      client: "Dobbli",
      country: "Serbia",
      city: "Belgrade",
      engagement: "3D Virtual Tours & Scene Reconstruction",
      description: "Built an end-to-end 3D virtual tour pipeline for immersive property and space experiences. Delivered image refinement, computed photogeometry homography and polygonal meshes for extended 3D panoramic scenes, and ran full 3D reconstruction using 3D Gaussian Splatting (3DGS) alongside COLMAP dense maps for high-fidelity spatial capture.",
      tags: ["3D Gaussian Splatting", "COLMAP", "Dense Maps", "Photogeometry", "Homography", "Polygonal Meshes", "Panorama", "Image Refinement"]
    },
    {
      index: "02",
      client: "Confidential",
      country: "Spain",
      city: "Barcelona",
      engagement: "Generative Furniture Redesign & LoRA Training Platform",
      description: "Architected an extensible LoRA training platform for high-fidelity furniture and sofa redesign. Built on a ComfyUI inference backend with Kohya LoRA DreamBooth training pipelines, enabling rapid iteration over product styles, materials, and configurations without reshooting physical products.",
      tags: ["ComfyUI", "Kohya LoRA", "DreamBooth", "LoRA Training", "Furniture Redesign", "Generative AI", "Product Imagery"]
    },
    {
      index: "03",
      client: "Axel",
      country: "Sweden",
      city: "Stockholm",
      engagement: "Hyperrealistic Product Imagery — Mockberg, Marshal & Others",
      description: "Delivered hyperrealistic AI-generated product photography for four Scandinavian lifestyle brands including Mockberg and Marshal. Produced on-brand visuals indistinguishable from studio shots, replacing costly physical shoots with generative workflows tuned per brand identity and material language.",
      tags: ["Hyperrealistic Rendering", "Product Photography", "Brand AI", "Generative Workflows", "Mockberg", "Marshal", "Style Fidelity"]
    }
  ];

  const approach = [
    {
      phase: "Discovery",
      description: "We begin with deep technical discovery, analyzing your challenges at the architectural level. Through stakeholder interviews and system audits, we identify opportunities for AI integration and map out potential impact areas."
    },
    {
      phase: "Architecture",
      description: "Designing bespoke AI systems that integrate seamlessly with your infrastructure. We create detailed technical specifications, data pipelines, and model architectures—ensuring scalability and maintainability from day one."
    },
    {
      phase: "Development",
      description: "Agile implementation with continuous validation. Our team builds production-ready systems using industry best practices, with rigorous testing at every stage. We maintain transparency through regular demos and technical documentation."
    },
    {
      phase: "Deployment",
      description: "Smooth transition to production with comprehensive monitoring and support. We handle infrastructure setup, performance optimization, and team training—ensuring your systems run reliably at scale with ongoing improvements."
    }
  ];

  return (
    <div className="min-h-screen bg-[#f7f4ed] dark:bg-[#0f0f0f] text-[#2c2416] dark:text-[#e8e6e3] transition-colors duration-500">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-40 w-96 h-96 bg-[#2c2416]/[0.015] dark:bg-[#e8e6e3]/[0.015] rounded-full blur-3xl animate-[float_20s_ease-in-out_infinite]" />
        <div className="absolute bottom-1/4 -right-40 w-96 h-96 bg-[#2c2416]/[0.01] dark:bg-[#e8e6e3]/[0.01] rounded-full blur-3xl animate-[float_25s_ease-in-out_infinite_reverse]" />
      </div>

      <nav className="absolute top-0 w-full z-50 py-8 px-8 lg:px-12">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <div className="opacity-0 animate-[fadeIn_0.8s_ease-out_forwards]">
            <img
              src="/landing%20page2.png"
              alt="KhyaalTech"
              className="h-16 md:h-20 w-auto object-contain scale-[1.3] origin-left dark:invert"
            />
          </div>
          
          <div className="hidden lg:flex items-center gap-12 text-[13px] font-light tracking-wide opacity-0 animate-[fadeIn_0.8s_ease-out_0.2s_forwards]">
            <a href="#services" className="hover:opacity-60 transition-colors duration-300">SERVICES</a>
            <a href="#work" className="hover:opacity-60 transition-colors duration-300">WORK</a>
            <a href="#portfolio" className="hover:opacity-60 transition-colors duration-300">PORTFOLIO</a>
            <a href="#approach" className="hover:opacity-60 transition-colors duration-300">APPROACH</a>
            <a href="#technology" className="hover:opacity-60 transition-colors duration-300">TECHNOLOGY</a>
            <a href="#partners" className="hover:opacity-60 transition-colors duration-300">CLIENTS</a>
          </div>

          <div className="flex items-center gap-4 opacity-0 animate-[fadeIn_0.8s_ease-out_0.4s_forwards]">
            <button 
              onClick={toggleTheme}
              className="p-2 rounded-full border border-[#2c2416]/10 dark:border-[#e8e6e3]/10 hover:bg-[#2c2416]/5 dark:hover:bg-[#e8e6e3]/5 transition-all duration-500 hover:scale-110"
              aria-label="Toggle theme"
            >
              {actualTheme === 'dark' ? <Sun className="w-4 h-4 transition-transform duration-500 hover:rotate-180" strokeWidth={1.5} /> : <Moon className="w-4 h-4 transition-transform duration-500 hover:rotate-12" strokeWidth={1.5} />}
            </button>
            <button 
              onClick={() => setTetrisOpen(true)}
              className="hidden sm:inline-flex items-center text-xs tracking-wider bg-[#2c2416]/10 dark:bg-[#e8e6e3]/10 text-[#2c2416] dark:text-[#e8e6e3] px-3 py-1 rounded-full border border-[#2c2416]/20 dark:border-[#e8e6e3]/20 hover:bg-[#2c2416]/20 dark:hover:bg-[#e8e6e3]/20 transition-all duration-500"
              aria-label="Play Tetris"
            >
              60s Tetris • Win Perks
            </button>
            <button
  onClick={() => setFlappyOpen(true)}
  className="block lg:hidden orientation-portrait fixed bottom-6 right-6 text-xs tracking-wider bg-[#2c2416]/10 dark:bg-[#e8e6e3]/10 text-[#2c2416] dark:text-[#e8e6e3] px-3 py-1 rounded-full border border-[#2c2416]/20 dark:border-[#e8e6e3]/20 hover:bg-[#2c2416]/20 dark:hover:bg-[#e8e6e3]/20 transition-all z-40"
  aria-label="Play Flappy Bird"
>
  Tap Bird • Win Perks
</button>
<button 
  onClick={() => setProjectModalOpen(true)}  // Opens the form modal
  className="group flex items-center gap-2 text-[13px] font-light tracking-wide hover:gap-3 transition-all duration-500"
>
  START PROJECT
  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-500" strokeWidth={1.5} />
</button>
          </div>
        </div>
      </nav>

      <WorldHero counters={counters} onStartProject={() => setProjectModalOpen(true)} />

      <div className="relative z-10 -mt-[100vh] bg-[#f7f4ed] dark:bg-[#0f0f0f] rounded-t-[32px] shadow-[0_-24px_60px_-20px_rgba(44,36,22,0.18)] dark:shadow-[0_-24px_60px_-20px_rgba(0,0,0,0.6)]">

      <section id="services" className="py-32 px-8 lg:px-12 border-t border-[#2c2416]/5 dark:border-[#e8e6e3]/5">
        <div className="max-w-[1200px] mx-auto">
          <div 
            data-animate 
            id="services-header"
            className={`mb-24 transition-all duration-1000 ${isVisible('services-header') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <p className="text-[11px] tracking-widest uppercase opacity-60 mb-4">SERVICES</p>
            <h2 className="text-[clamp(2rem,5vw,3.5rem)] font-extralight leading-tight tracking-tight mb-6">
              Precision-engineered AI solutions
            </h2>
            <p className="text-[15px] text-[#2c2416]/90 dark:text-[#e8e6e3]/85 font-light max-w-[600px]">
              From computer vision to autonomous systems, we deliver production-ready AI that solves real business problems.
            </p>
          </div>

          <div className="space-y-24">
            {services.map((service, idx) => {
              const Icon = service.icon;
              return (
                <div 
                  key={idx} 
                  data-animate
                  id={`service-${idx}`}
                  className={`border-t border-[#2c2416]/5 dark:border-[#e8e6e3]/5 pt-12 group transition-all duration-1000 ${isVisible(`service-${idx}`) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                >
                  <div className="flex items-start gap-6 mb-6">
                    <div className="flex-1">
                      <div className="flex items-center gap-5 mb-4">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#2c2416]/[0.03] to-[#2c2416]/[0.08] dark:from-[#e8e6e3]/[0.03] dark:to-[#e8e6e3]/[0.08] flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-700 ease-out">
                          <Icon className="w-5 h-5 opacity-60 group-hover:opacity-100 transition-opacity duration-700" strokeWidth={1.5} />
                        </div>
                        <h3 className="text-[28px] font-medium tracking-tight group-hover:opacity-80 transition-opacity duration-700">{service.title}</h3>
                      </div>
                      <p className="text-[15px] leading-relaxed text-[#2c2416]/90 dark:text-[#e8e6e3]/85 font-light mb-6 max-w-[800px] group-hover:opacity-95 transition-opacity duration-700">
                        {service.description}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {service.capabilities.map((cap, i) => (
                          <span key={i} className="text-[11px] px-3 py-1 border border-[#2c2416]/20 dark:border-[#e8e6e3]/20 rounded-full tracking-wide font-light opacity-80 hover:opacity-100 hover:border-[#2c2416]/40 dark:hover:border-[#e8e6e3]/40 hover:scale-105 transition-all duration-500">
                            {cap}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section id="work" className="py-32 px-8 lg:px-12 border-t border-[#2c2416]/5 dark:border-[#e8e6e3]/5 bg-[#2c2416]/[0.015] dark:bg-[#e8e6e3]/[0.015]">
        <div className="max-w-[1200px] mx-auto">
          <div
            data-animate
            id="work-header"
            className={`mb-24 transition-all duration-1000 ${isVisible('work-header') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <p className="text-[11px] tracking-widest uppercase opacity-60 mb-4">FEATURED PROJECT</p>
            <h2 className="text-[clamp(2rem,5vw,3.5rem)] font-extralight leading-tight tracking-tight mb-6">
              SynthralOS
            </h2>
            <p className="text-[15px] text-[#2c2416]/90 dark:text-[#e8e6e3]/85 font-light max-w-[720px] leading-relaxed">
              A multi-tenant AI agent operating system—co-architected and owned end-to-end. SynthralOS unifies the fragmented agent ecosystem behind a single, production-grade platform: from runtime to memory, retrieval to deployment.
            </p>
          </div>

          <div className="space-y-0">
            {[
              {
                index: "01",
                title: "Agent Runtime",
                detail: "14 polymorphic framework adapters — ReAct, CrewAI, AutoGen, MetaGPT, AutoGPT, Camel, Swarm, AgentGPT, Archon, Riona, Kush, Kyro, and others — behind a unified dispatcher with spawn-prevention guardrails and bounded execution.",
                tags: ["14 Framework Adapters", "Unified Dispatcher", "Spawn Guards", "Bounded Execution"]
              },
              {
                index: "02",
                title: "Workflow Engine",
                detail: "Durable execution combining a Temporal-style executor with LangGraph orchestration. Supports versioned workflows, deterministic replay, configurable retry policies, dry-run mode, rollback, and three-state approval handlers.",
                tags: ["Temporal-Style Executor", "LangGraph", "Versioned Workflows", "Replay & Rollback", "Approval Handlers"]
              },
              {
                index: "03",
                title: "Three-Tier Memory Stack",
                detail: "Long-term persistence via Mem0, relational knowledge via Graphiti (knowledge graph), and in-flight caching via Context7 — unified behind a single adapter with time-boxed reads and fire-and-forget async writes.",
                tags: ["Mem0", "Graphiti", "Context7", "Time-Boxed Reads", "Async Writes"]
              },
              {
                index: "04",
                title: "RAG Layer",
                detail: "Postgres-canonical retrieval with Qdrant-indexed vector search, rebuildable indices on demand, and pgvector fallback for maximum resilience. PGVector-backed skills and connector registries complete the retrieval surface.",
                tags: ["Qdrant", "pgvector", "Rebuildable Indices", "Skills Registry", "Connector Registry"]
              },
              {
                index: "05",
                title: "Infrastructure & Deployment",
                detail: "~40 Alembic migrations across a Postgres-canonical schema. Self-hosted production deployment on Coolify + Render with a self-managed Mem0 + Qdrant network. Full API surface and frontend owned end-to-end.",
                tags: ["Coolify", "Render", "Alembic", "Self-Hosted", "Full-Stack Ownership"]
              }
            ].map((item, idx) => (
              <div
                key={idx}
                data-animate
                id={`work-item-${idx}`}
                className={`border-t border-[#2c2416]/8 dark:border-[#e8e6e3]/8 pt-10 pb-10 group transition-all duration-1000 ${isVisible(`work-item-${idx}`) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              >
                <div className="flex flex-col md:flex-row md:items-start gap-6 md:gap-12">
                  <span className="text-[11px] tracking-widest opacity-30 font-light shrink-0 pt-1 group-hover:opacity-60 transition-opacity duration-300">{item.index}</span>
                  <div className="flex-1">
                    <h3 className="text-[22px] font-medium tracking-tight mb-3 group-hover:opacity-80 transition-opacity duration-300">{item.title}</h3>
                    <p className="text-[14px] leading-relaxed text-[#2c2416]/80 dark:text-[#e8e6e3]/75 font-light mb-5 max-w-[820px]">
                      {item.detail}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {item.tags.map((tag, i) => (
                        <span key={i} className="text-[10px] px-3 py-1 border border-[#2c2416]/20 dark:border-[#e8e6e3]/20 rounded-full tracking-widest font-light uppercase opacity-70 hover:opacity-100 transition-opacity duration-300">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ProjectPortfolio />

      <section id="approach" className="py-32 px-8 lg:px-12 bg-[#2c2416]/[0.01] dark:bg-[#e8e6e3]/[0.01]">
        <div className="max-w-[1200px] mx-auto">
          <div 
            data-animate
            id="approach-header"
            className={`mb-24 transition-all duration-1000 ${isVisible('approach-header') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <p className="text-[11px] tracking-widest uppercase opacity-60 mb-4">METHODOLOGY</p>
            <h2 className="text-[clamp(2rem,5vw,3.5rem)] font-extralight leading-tight tracking-tight mb-6">
              Disciplined approach to innovation
            </h2>
            <p className="text-[15px] text-[#2c2416]/90 dark:text-[#e8e6e3]/85 font-light max-w-[600px]">
              Every project follows a structured methodology that ensures technical rigor and business alignment.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {approach.map((step, idx) => (
              <div 
                key={idx}
                data-animate
                id={`approach-${idx}`}
                className={`border-l border-[#2c2416]/10 dark:border-[#e8e6e3]/10 pl-8 group hover:border-[#2c2416]/20 dark:hover:border-[#e8e6e3]/20 transition-all duration-1000 ${isVisible(`approach-${idx}`) ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
              >
                <p className="text-[11px] tracking-widest uppercase opacity-60 mb-3 font-medium group-hover:opacity-80 transition-opacity duration-300">{step.phase}</p>
                <p className="text-[15px] leading-relaxed text-[#2c2416]/90 dark:text-[#e8e6e3]/85 font-light group-hover:opacity-95 transition-opacity duration-300">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="technology" className="py-32 px-8 lg:px-12 border-t border-[#2c2416]/5 dark:border-[#e8e6e3]/5">
        <div className="max-w-[1200px] mx-auto">
          <div 
            data-animate
            id="tech-header"
            className={`mb-24 transition-all duration-1000 ${isVisible('tech-header') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <p className="text-[11px] tracking-widest uppercase opacity-60 mb-4">TECHNOLOGY STACK</p>
            <h2 className="text-[clamp(2rem,5vw,3.5rem)] font-extralight leading-tight tracking-tight mb-6">
              Industry-standard tools & frameworks
            </h2>
            <p className="text-[15px] text-[#2c2416]/90 dark:text-[#e8e6e3]/85 font-light max-w-[600px]">
              We leverage cutting-edge platforms and battle-tested technologies to deliver solutions that scale.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-8">
            {technologies.map((tech, idx) => (
              <div 
                key={idx}
                data-animate
                id={`tech-${idx}`}
                className={`transition-all duration-1000 ${isVisible(`tech-${idx}`) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                <p className="text-[13px] font-semibold mb-4 opacity-90 hover:opacity-100 transition-opacity duration-300">{tech.category}</p>
                <div className="space-y-2">
                  {tech.items.map((item, i) => (
                    <p key={i} className="text-[13px] text-[#2c2416]/75 dark:text-[#e8e6e3]/75 font-light hover:text-[#2c2416] dark:hover:text-[#e8e6e3] transition-colors duration-300 cursor-default">{item}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="partners" className="py-32 px-8 lg:px-12 bg-[#2c2416]/[0.01] dark:bg-[#e8e6e3]/[0.01]">
        <div className="max-w-[1200px] mx-auto">
          <div
            data-animate
            id="partners-header"
            className={`mb-24 transition-all duration-1000 ${isVisible('partners-header') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <p className="text-[11px] tracking-widest uppercase opacity-60 mb-4">SELECTED CLIENTS</p>
            <h2 className="text-[clamp(2rem,5vw,3.5rem)] font-extralight leading-tight tracking-tight mb-6">
              Work that ships
            </h2>
            <p className="text-[15px] text-[#2c2416]/90 dark:text-[#e8e6e3]/85 font-light max-w-[600px]">
              A cross-section of real engagements—each one a different domain, the same standard of precision.
            </p>
          </div>

          <div className="space-y-0">
            {clients.map((c, idx) => (
              <div
                key={idx}
                data-animate
                id={`partner-${idx}`}
                className={`border-t border-[#2c2416]/5 dark:border-[#e8e6e3]/5 pt-12 pb-12 group transition-all duration-1000 ${isVisible(`partner-${idx}`) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              >
                <div className="flex flex-col md:flex-row md:items-start gap-6 md:gap-12">
                  <span className="text-[11px] tracking-widest opacity-30 font-light shrink-0 pt-1 group-hover:opacity-60 transition-opacity duration-300">{c.index}</span>
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-4 mb-1">
                      <h3 className="text-[22px] font-medium tracking-tight group-hover:opacity-80 transition-opacity duration-300">{c.client}</h3>
                      <span className="text-[12px] text-[#2c2416]/50 dark:text-[#e8e6e3]/50 font-light tracking-wide">{c.city}, {c.country}</span>
                    </div>
                    <p className="text-[12px] tracking-widest uppercase opacity-50 mb-4 font-light">{c.engagement}</p>
                    <p className="text-[14px] leading-relaxed text-[#2c2416]/80 dark:text-[#e8e6e3]/75 font-light mb-5 max-w-[820px]">
                      {c.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {c.tags.map((tag, i) => (
                        <span key={i} className="text-[10px] px-3 py-1 border border-[#2c2416]/20 dark:border-[#e8e6e3]/20 rounded-full tracking-widest font-light uppercase opacity-70 hover:opacity-100 transition-opacity duration-300">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section 
        data-animate
        id="testimonial"
        className={`py-32 px-8 lg:px-12 border-t border-[#2c2416]/5 dark:border-[#e8e6e3]/5 transition-all duration-1000 ${isVisible('testimonial') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className="max-w-[900px] mx-auto">
          <div className="relative rounded-3xl backdrop-blur-xl bg-[#2c2416]/[0.02] dark:bg-[#e8e6e3]/[0.02] border border-[#2c2416]/10 dark:border-[#e8e6e3]/10 p-12 lg:p-16 shadow-2xl shadow-[#2c2416]/5 dark:shadow-[#e8e6e3]/5">
            <div className="absolute -top-6 left-12">
              <div className="w-12 h-12 rounded-2xl backdrop-blur-xl bg-[#2c2416]/5 dark:bg-[#e8e6e3]/5 border border-[#2c2416]/10 dark:border-[#e8e6e3]/10 flex items-center justify-center">
                <span className="text-3xl opacity-60">"</span>
              </div>
            </div>
            <p className="text-[11px] tracking-widest uppercase opacity-60 mb-8 text-center">CLIENT TESTIMONIAL</p>
            <blockquote className="text-[clamp(1.2rem,3vw,1.8rem)] font-light leading-relaxed mb-12 tracking-tight text-center">
              KhyaalTech delivered a computer vision system that exceeded our expectations. Their technical depth, attention to detail, and commitment to excellence is unmatched in the industry. The team's ability to translate complex requirements into production-ready solutions was remarkable.
            </blockquote>
            <div className="text-center">
              <p className="text-[15px] font-medium mb-1">Marcus Lindberg</p>
              <p className="text-[13px] text-[#2c2416]/70 dark:text-[#e8e6e3]/70 font-light tracking-wide">CTO, Nordic Vision AB · Stockholm</p>
            </div>
          </div>
        </div>
      </section>

      <section 
        data-animate
        id="cta"
        className={`py-32 px-8 lg:px-12 border-t border-[#2c2416]/5 dark:border-[#e8e6e3]/5 transition-all duration-1000 ${isVisible('cta') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className="max-w-[1000px] mx-auto text-center">
          <h2 className="text-[clamp(2.5rem,6vw,5rem)] font-extralight leading-tight tracking-tight mb-8">
            Let's build something
            <br />
            exceptional together
          </h2>
          <p className="text-[15px] text-[#2c2416]/90 dark:text-[#e8e6e3]/85 font-light mb-12">
            Schedule a technical consultation to discuss your vision requirements
          </p>
          <button 
  onClick={() => {
    const subject = 'KhyaalTech Consultation Request';
    const body = 'Hi Asad,\n\nI\'m interested in a consultation. Here\'s a bit about me:\n\n[Your details here]\n\nLooking forward to chatting!\n\nBest,\n[Your Name]';
    window.location.href = `mailto:asad.haider.rizvi64@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }} 
  className="...">
  INITIATE CONSULTATION
</button>
        </div>
      </section>
      {projectModalOpen && (
  <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div data-lenis-prevent className="bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-light">Start a Project</h2>
        <button onClick={() => setProjectModalOpen(false)} className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700">
          <X className="w-5 h-5" />
        </button>
      </div>
      <form onSubmit={(e) => {
        e.preventDefault();
        const subject = encodeURIComponent(`Project Request from ${projectForm.name}`);
        const body = encodeURIComponent(`Name: ${projectForm.name}\nEmail: ${projectForm.email}\n\nProject Details:\n${projectForm.details}`);
        window.location.href = `mailto:asad.haider.rizvi64@gmail.com?subject=${subject}&body=${body}`;
        setProjectModalOpen(false);
        setProjectForm({ name: '', email: '', details: '' });
      }}>
        <input
          type="text"
          placeholder="Your Name"
          value={projectForm.name}
          onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
          className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg mb-3 bg-transparent"
          required
        />
        <input
          type="email"
          placeholder="Your Email"
          value={projectForm.email}
          onChange={(e) => setProjectForm({ ...projectForm, email: e.target.value })}
          className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg mb-3 bg-transparent"
          required
        />
        <textarea
          placeholder="Project Details (e.g., what do you need built?)"
          value={projectForm.details}
          onChange={(e) => setProjectForm({ ...projectForm, details: e.target.value })}
          rows={4}
          className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg mb-3 bg-transparent"
          required
        />
        <button type="submit" className="w-full p-3 bg-[#2c2416] dark:bg-[#e8e6e3] text-white dark:text-black rounded-lg hover:opacity-90 transition">
          Submit Request
        </button>
      </form>
    </div>
  </div>
)}

      <footer className="border-t border-[#2c2416]/5 dark:border-[#e8e6e3]/5 py-12 px-8 lg:px-12">
        <div className="max-w-[1400px] mx-auto flex flex-col lg:flex-row justify-between items-center gap-8">
          <div className="opacity-40">
            <img
              src="/landing%20page2.png"
              alt="KhyaalTech"
              className="h-10 md:h-12 w-auto object-contain scale-[1.3] origin-left dark:invert"
            />
          </div>
          
          <div className="flex items-center gap-8 text-[11px] tracking-widest opacity-40 font-light">
            <span>© 2024</span>
            <a href="/privacy" className="hover:opacity-100 transition-opacity duration-300">PRIVACY</a>
            <a href="/terms" className="hover:opacity-100 transition-opacity duration-300">TERMS</a>
          </div>
        </div>
      </footer>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
        }
        @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        @keyframes float {
            0%, 100% { transform: translate(0, 0) scale(1); }
            33% { transform: translate(30px, -30px) scale(1.05); }
            66% { transform: translate(-20px, 20px) scale(0.95); }
        }
      `}</style>

      {tetrisOpen && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">Loading game...</div>}>
          <TetrisModal onClose={() => setTetrisOpen(false)} />
        </Suspense>
      )}
    {flappyOpen && (
    <Suspense fallback={<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">Loading game...</div>}>
        <FlappyBirdModal onClose={() => setFlappyOpen(false)} />
    </Suspense>
    )}
    </div>
  );
}